import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Home as HomeIcon, MessageCircle, FileText, CheckCircle2 } from "lucide-react";

export default function EvaluationSection() {
    return (
        <section
            data-testid="evaluation-section"
            className="relative overflow-hidden py-20 lg:py-28"
            style={{ background: "linear-gradient(180deg,#FBF8F1 0%,#F8F5EE 100%)" }}
        >
            <div className="max-w-6xl mx-auto px-5 lg:px-8">
                <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.7, ease: [0.2,0.7,0.2,1] }}
                    >
                        <div className="eyebrow">Notre approche</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#041126] mt-3 leading-[1.1]">
                            Une évaluation gratuite,<br className="hidden sm:block"/> pour une soumission réellement adaptée.
                        </h2>
                        <div className="mt-6 h-px w-24" style={{ background: "linear-gradient(90deg,transparent,#D4AF37,transparent)" }}/>
                        <div className="mt-6 space-y-4 text-slate-700 leading-relaxed text-[15px] sm:text-base">
                            <p>Chaque espace est différent, tout comme vos besoins.</p>
                            <p>C'est pourquoi nous privilégions une <strong className="text-[#041126]">évaluation gratuite sur place</strong> avant de préparer votre soumission. Cette rencontre nous permet de découvrir les lieux, de discuter de vos attentes, de vos priorités et de la fréquence de service recherchée.</p>
                            <p>Nous pouvons ensuite vous proposer une <strong className="text-[#041126]">soumission personnalisée</strong>, adaptée à votre environnement et au niveau d'entretien réellement nécessaire.</p>
                        </div>

                        {/* Reassurance line */}
                        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
                            {["Évaluation gratuite", "Sans engagement", "Soumission personnalisée"].map((t, i) => (
                                <span key={t} className="inline-flex items-center gap-2 text-[#041126]">
                                    <CheckCircle2 className="w-4 h-4" style={{ color: i === 0 ? "#008CFF" : i === 1 ? "#00B8FF" : "#39D600" }}/>
                                    <span>{t}</span>
                                </span>
                            ))}
                        </div>

                        <div className="mt-8">
                            <Link to="/soumission" className="btn-kgd" data-testid="evaluation-cta">
                                <Sparkles className="w-4 h-4"/> Planifier mon évaluation gratuite
                            </Link>
                            <p className="mt-3 text-xs sm:text-sm text-slate-500">Visite des lieux, discussion de vos besoins et soumission personnalisée.</p>
                        </div>
                    </motion.div>

                    {/* Right visual — process card */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.8, delay: 0.15, ease: [0.2,0.7,0.2,1] }}
                        className="relative"
                    >
                        <div className="absolute -inset-4 rounded-3xl blur-3xl opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 30%, rgba(7,91,200,0.35), transparent 60%), radial-gradient(circle at 70% 70%, rgba(19,138,61,0.28), transparent 60%)" }}/>
                        <div className="relative luxe-card p-6 sm:p-8">
                            <div className="eyebrow">Le processus Ménage KGD</div>
                            <div className="mt-5 space-y-4">
                                {[
                                    { icon: MessageCircle, t: "Prise de contact", d: "Vous nous parlez brièvement de votre espace et de vos priorités." },
                                    { icon: HomeIcon, t: "Évaluation gratuite sur place", d: "Nous nous déplaçons pour découvrir les lieux et discuter de vos attentes." },
                                    { icon: FileText, t: "Soumission personnalisée", d: "Nous vous préparons une proposition adaptée à votre environnement." },
                                    { icon: Sparkles, t: "Service adapté et constant", d: "Une équipe attentive, discrète et respectueuse de votre espace." },
                                ].map((s, i) => (
                                    <div key={s.t} className="flex items-start gap-4">
                                        <div className="relative flex-shrink-0">
                                            <div className="w-11 h-11 rounded-full flex items-center justify-center text-[#041126] shadow-[0_6px_18px_-6px_rgba(212,175,55,0.55),inset_0_1px_0_rgba(255,255,255,0.55)]" style={{ background: "linear-gradient(135deg,#E8D48B,#D4AF37 55%,#B8912B)" }}>
                                                <s.icon className="w-5 h-5" strokeWidth={1.75}/>
                                            </div>
                                            {i < 3 && <span className="absolute left-1/2 top-full -translate-x-1/2 mt-1 h-4 w-px bg-kgd-gold/40"/>}
                                        </div>
                                        <div className="pt-1">
                                            <div className="font-serif text-lg text-[#041126]">{s.t}</div>
                                            <div className="text-sm text-slate-600 leading-relaxed mt-0.5">{s.d}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
